import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { provenanceDate, type FieldProvenance } from '@/lib/fieldProvenance';
import { IntakeFieldInput } from '@/components/intake/IntakeFieldInput';
import { fmtDateForDisplay, get, instantAnswerDay, resolveFieldLabel, type FieldSpec, type FileAnswer } from '@/lib/intakeFields';
import { optionLabelFor } from '@/lib/formDefinitions';
import { TranslatedAnswer } from '@/components/trfer/TranslatedAnswer';
import { isNameField, isTranslatableAnswerField } from '@/components/trfer/translatedAnswerLookup';
import { useLocale } from '@/i18n/useLocale';
import { msg, resolveMsg, type AnyTFunction } from '@/i18n/msg';
import { formatMoneyAnswer } from '@/lib/moneyAnswer';
import type { AnswerTranslation, FieldEditRequest, FieldEditStalePath } from '@/types/applications';

/**
 * The one staff field editor — ONE copy, shared by the application detail page
 * and the family intake drawer (D-SE-12, "one editor, reused").
 *
 * Extracted in PR-3 rather than copied into the drawer. What makes this
 * shared-by-construction rather than shared-by-tidiness is `openedAgainst`
 * below: it is the CAS `before` the server refuses a stale write against
 * (D-SE-4), and a second copy of it is a second chance to re-read it at save
 * time — which is exactly the last-writer-silently-wins defect the design's
 * first revision shipped. Two surfaces editing one engine get one row.
 */

/**
 * `locale` (#2109 R2 L5) is the BCP 47 tag a stored date is formatted in — the
 * reader's UI locale. Omitted, the runtime default applies, as before.
 *
 * `t` (ported from #2109 R4 L0 for the family review screen): when passed,
 * "Yes", "No" and "Uploaded file" resolve through `common:answers.*` in the
 * reader's language. Omitted — every staff caller, which is English-only by
 * DECISIONS 2026-09-30 — the output is the English it always was.
 */
export function formatFieldValue(
  value: unknown,
  field: Pick<FieldSpec, 'type' | 'optionLabels'>,
  locale?: string,
  t?: AnyTFunction,
): string {
  const word = (key: 'yes' | 'no' | 'uploadedFile', english: string): string =>
    t ? resolveMsg(t, msg(`common:answers.${key}`)) : english;
  const type = field.type;
  if (value === undefined || value === null || value === '') return '—';
  // D-FB-12: a file answer is `{ source_document_id, filename }`. It must be
  // read BEFORE the generic branches below — the last of them is
  // `String(value)`, which renders every object as "[object Object]" on both
  // staff surfaces that share this formatter.
  if (type === 'file') {
    const answer = value as Partial<FileAnswer>;
    if (!answer?.source_document_id) return '—';
    return answer.filename || word('uploadedFile', 'Uploaded file');
  }
  if (typeof value === 'boolean') return value ? word('yes', 'Yes') : word('no', 'No');
  // #1011: a choice answer reads as the words the family chose, not the minted
  // value. An unmatched value passes through, so a legacy answer is never lost.
  const label = (v: unknown) => (typeof v === 'string' ? optionLabelFor(field, v) : String(v));
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value.map(label).join(', ');
  }
  if (type === 'date' && typeof value === 'string') {
    // B-432: formatted in the zone it was STORED in. Reading it as an instant
    // and converting to the viewer's zone shows the previous day everywhere
    // west of UTC — and disagrees with the date input opened from this row.
    const shown = fmtDateForDisplay(value, locale);
    if (shown) return shown;
  }
  if (type === 'number') return String(value);
  if (type === 'checkbox') return value ? word('yes', 'Yes') : word('no', 'No');
  return label(value);
}

/**
 * `formatFieldValue` for a stored answer at its path (`section.key`). #2704: an
 * instant-valued date field (`intake.applied_at`, stamped at submit) prints the
 * viewer's day, the day the page header names; its UTC day put a 9:52 PM
 * Eastern submit on the next day. A calendar day keeps the day it names.
 */
function answerAtPath(path: string, value: unknown, field: Pick<FieldSpec, 'type' | 'optionLabels'>, locale?: string): string {
  return instantAnswerDay(path, value, field) ?? formatFieldValue(value, field, locale);
}

/**
 * #2109 R2-B3: only free-text answers are ever translated. Choice answers are
 * stored as option values and read through the English labels, so a
 * translation passed for one is ignored.
 */
function isProse(field: Pick<FieldSpec, 'type'>): boolean {
  return field.type === 'text' || field.type === 'textarea';
}

/** D-SE-13: a retired question keeps its stored answer, and stays correctable. */
export const RETIRED_NOTE = 'TRF no longer asks this question. You can still correct the stored answer.';

interface EditableFieldRowProps {
  field: FieldSpec;
  /** The section sub-document — also the sibling source for conditional labels. */
  data: Record<string, unknown> | undefined;
  /** Dotted path prefix; `field.key` is relative to its section. */
  sectionKey: string;
  /**
   * #602 — every section's values, keyed by section id, for a field whose
   * option list is driven from ANOTHER section (`patient.ethnicity` swaps on
   * `intake.country`). Optional: without it the editor falls back to the
   * field's static list, which is the behaviour every caller had before.
   */
  sectionValues?: Record<string, Record<string, unknown> | undefined>;
  retired?: boolean;
  onSave: (change: FieldEditRequest) => void;
  /** The path currently in flight, so only its own row shows a pending state. */
  savingPath: string | null;
  /** Set when the server refused this path's write: what is actually on file. */
  conflict?: FieldEditStalePath;
  onDismissConflict: (path: string) => void;
  /**
   * Bumped by the parent when a write for THIS path landed (B-449).
   *
   * It has to be a token rather than a boolean or the stored value: the row
   * needs to distinguish "the write landed" from "a refetch arrived", and two
   * successive saves of the same value produce identical data. Only a landed
   * write moves it — a 500 leaves it alone, so a failed save keeps the editor
   * open and the staff member's draft with it.
   */
  savedToken?: number;
  /**
   * 2026-09-15 (review page, option 1 "answered first"). `stacked` is the
   * original label-over-value pair the family drawer still uses. `row` is
   * the review page's property-list row: a fixed label column, the value
   * beside it in one fixation, a pencil that shows on hover or focus, long
   * text clamped at two lines behind a visible Show more.
   */
  variant?: 'stacked' | 'row';
  /** Row variant only: shown under the answer (2026-10-11 Option A: the AI review's markers). */
  annotation?: React.ReactNode;
  /**
   * #2719: the currency symbol a money answer prints with ("$" or "CA$"). Set
   * by the caller for the dollar answers only; the stored number is untouched.
   */
  moneySymbol?: string | null;
  /** The short staff label to PRINT (`staffLabel()`); the family's full
   *  question stays the accessible name of the edit control and the label's
   *  title, so nothing the family was asked is lost. Default: the question. */
  displayLabel?: string;
  /** The last staff correction to this path, from the change history — rendered
   *  under the value as "Changed by Ana on Aug 3, 2026 from …" (option 2's line). */
  provenance?: FieldProvenance;
  /**
   * #2109 R2-B2: the English machine translation of this answer
   * (`answer_translations[section][field].en`). Shown only while its
   * `source_hash` matches the answer on file, so an edit hides it (R2-B14).
   */
  translation?: AnswerTranslation | null;
  /** The language the family answered in (the application's `language`). */
  language?: string | null;
  /** TranslatedAnswer `onTranslate`: "Translate to English" beside "Not translated yet". */
  onTranslate?: () => Promise<unknown>;
}

/** The row variant's value-as-edit-control. */
const EDIT_VALUE_CLASS = '-mx-1 block w-full rounded-md px-1 py-1 text-left text-body text-foreground hover:bg-muted/40 focus-visible:bg-muted/40';

/** A textarea answer longer than this is clamped at two lines with Show more. */
const CLAMP_CHARS = 160;

// Exported for its own test: the snapshot property below cannot be observed
// from the page, because reaching it needs the stored value to CHANGE while the
// editor is open — which is precisely the concurrent write D-SE-4 exists for.
export function EditableFieldRow({
  field, data, sectionKey, sectionValues, retired = false, onSave, savingPath, conflict, onDismissConflict,
  savedToken = 0, variant = 'stacked', displayLabel, moneySymbol, provenance, translation, language, onTranslate, annotation,
}: EditableFieldRowProps) {
  const { bcp47 } = useLocale();
  const path = `${sectionKey}.${field.key}`;
  const stored = get(data, field.key);
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<unknown>(stored);
  /**
   * The value the editor was OPENED against — snapshotted, not re-read.
   *
   * This is what the server compares against what is on file (D-SE-4). Reading
   * it fresh at save time would make the conflict check satisfied by
   * construction and silently reinstate last-writer-wins, which is the exact
   * defect the design's first revision shipped.
   */
  const [openedAgainst, setOpenedAgainst] = useState<unknown>(stored);
  const saving = savingPath === path;

  const open = () => {
    setOpenedAgainst(stored);
    setDraft(stored);
    setEditing(true);
    onDismissConflict(path);
  };

  // A refused write closes the editor. The conflict message and the value now
  // on file both live in the VIEW row, so leaving the editor open would report
  // the conflict somewhere the staff member cannot see it while their own
  // rejected draft sits on screen looking authoritative.
  useEffect(() => { if (conflict) setEditing(false); }, [conflict]);

  /**
   * B-449: a landed write closes the editor.
   *
   * The `openedAgainst` snapshot is per-OPENING, so an editor left open across
   * a successful save carries a `before` that is now one write stale. Correct
   * the same field twice without closing and the second save asserts a value
   * the staff member themselves replaced — the server refuses a correct
   * correction, and the row quotes back the value they just saved as though a
   * colleague had written it. Closing returns the row to what is on file and
   * makes the next edit re-snapshot, which is the invariant the D-SE-4 tests
   * above already pin.
   */
  const seenToken = useRef(savedToken);
  useEffect(() => {
    if (savedToken !== seenToken.current) {
      seenToken.current = savedToken;
      setEditing(false);
    }
  }, [savedToken]);

  const cancel = () => { setEditing(false); setDraft(stored); };

  // Inline editing (2026-09-16): the input that replaces the value takes
  // focus as it appears, so the click that opened it is also the click that
  // lets the reader type (guideline rule 15, focus routing). Seen live: the
  // first keystrokes after opening a row went nowhere.
  const editorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!editing || variant !== 'row') return;
    const control = editorRef.current?.querySelector<HTMLElement>('input:not([type="hidden"]), textarea, select, [role="combobox"], button[role="checkbox"]');
    control?.focus();
    if (control instanceof HTMLInputElement && control.type === 'text') {
      const end = control.value.length;
      control.setSelectionRange(end, end);
    }
  }, [editing, variant]);

  if (editing && variant === 'stacked') {
    return (
      <div className="space-y-2 sm:col-span-2 rounded-md border border-border bg-muted/20 p-3">
        <IntakeFieldInput
          field={field}
          value={draft}
          onChange={setDraft}
          siblingValues={data}
          sectionValues={sectionValues}
        />
        {retired && <p className="text-caption text-stevens-ink-3 italic">{RETIRED_NOTE}</p>}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="brand"
            disabled={saving}
            onClick={() => onSave({ path, before: openedAgainst, after: draft })}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={cancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  const question = resolveFieldLabel(field, data, sectionValues);
  const shown = (moneySymbol && formatMoneyAnswer(stored, moneySymbol)) || answerAtPath(path, stored, field, bcp47);
  // #2109 R2: a prose answer with a string on file reads through
  // TranslatedAnswer; everything else prints `shown` exactly as before.
  const translatable = isProse(field) && typeof stored === 'string' && stored.trim() !== '';
  const isName = isNameField(field.key);
  // Victor 2026-09-30 R1: an identifier (an address, a phone) is never
  // translated, so it shows the original alone with no "Not translated yet".
  const answerTranslatable = isTranslatableAnswerField({ id: field.key, type: field.type, format: field.format });

  if (variant === 'row') {
    const long = field.type === 'textarea' && shown.length > CLAMP_CHARS;
    const provenanceWhen = provenance ? provenanceDate(provenance.at) : null;
    // 2026-09-16 (Victor): inline editing. The value itself is the control;
    // clicking it swaps the value for the field's input IN the row, the save
    // icon appears only once something changed, Enter saves, Escape cancels.
    // The explicit save stays (D-SE-4/D-SE-6): nothing is written on blur.
    const dirty = JSON.stringify(draft ?? null) !== JSON.stringify(openedAgainst ?? null);
    const save = () => { if (dirty && !saving) onSave({ path, before: openedAgainst, after: draft }); };
    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      if (e.key === 'Enter' && field.type !== 'textarea' && !(e.target instanceof HTMLButtonElement)) {
        e.preventDefault(); save();
      }
    };
    return (
      // data-field-key: the application review panel's "Show" finds an answer by it (2026-10-10).
      <div className="group flex items-start gap-4 border-b border-stevens-rule-2 py-2" data-testid={`field-row-${path}`} data-field-key={path}>
        <dt className="w-40 shrink-0 pt-1 text-body-sm text-stevens-ink-3" title={displayLabel && displayLabel !== question ? question : undefined}>
          {displayLabel ?? question}
        </dt>
        <dd className="m-0 min-w-0 flex-1 text-body text-foreground">
          {editing ? (
            <div
              ref={editorRef}
              className="flex items-start gap-2 [&_label]:sr-only [&_label+p]:sr-only"
              onKeyDown={onKeyDown}
              data-testid={`field-editor-${path}`}
            >
              <div className="min-w-0 flex-1">
                <IntakeFieldInput
                  field={field}
                  value={draft}
                  onChange={setDraft}
                  siblingValues={data}
                  sectionValues={sectionValues}
                />
              </div>
              {dirty && (
                <button
                  type="button"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-stevens-indigo-solid text-stevens-indigo-solid-fg disabled:opacity-60"
                  aria-label="Save"
                  title={saving ? 'Saving…' : 'Save'}
                  disabled={saving}
                  onClick={save}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
                </button>
              )}
              <button
                type="button"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-stevens-ink-3 hover:bg-muted/40 disabled:opacity-60"
                aria-label="Cancel"
                title="Cancel"
                disabled={saving}
                onClick={cancel}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ) : translatable ? (
            // #2109 R2 / Victor 2026-09-30 R1: the ORIGINAL sits inside the
            // edit control (it is what is edited); the English and its label
            // render beneath it, never nested in it.
            <TranslatedAnswer original={stored as string} translation={translation} language={language} name={isName} translatable={answerTranslatable} onTranslate={onTranslate}>
              {({ text, lang }) => (
                <button
                  type="button"
                  onClick={open}
                  aria-label={`Edit ${question}`}
                  title="Click to edit"
                  className={EDIT_VALUE_CLASS}
                >
                  <span lang={lang} translate={isName ? 'no' : undefined} className={long && !expanded ? 'line-clamp-2' : undefined}>{text}</span>
                </button>
              )}
            </TranslatedAnswer>
          ) : (
            <button
              type="button"
              onClick={open}
              aria-label={`Edit ${question}`}
              title="Click to edit"
              className={EDIT_VALUE_CLASS}
            >
              <span className={long && !expanded ? 'line-clamp-2' : undefined}>{shown}</span>
            </button>
          )}
          {long && !editing && (
            <button
              type="button"
              className="text-caption text-stevens-indigo underline underline-offset-2"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
          {provenance && (
            <p className="m-0 text-caption text-stevens-ink-3" data-testid={`provenance-${path}`}>
              Changed by {provenance.actor}
              {provenanceWhen ? ` on ${provenanceWhen}` : ''}
              {' '}from <s>{answerAtPath(path, provenance.before, field, bcp47)}</s>
              {provenance.note ? <>{' — '}“{provenance.note}”</> : null}
            </p>
          )}
          {retired && <p className="m-0 text-caption text-stevens-ink-3">{RETIRED_NOTE}</p>}
          {conflict && (
            <p role="alert" className="m-0 text-caption text-status-danger-fg">
              Not saved — this field holds{' '}
              <strong>{answerAtPath(path, conflict.found, field, bcp47)}</strong> on file. Reopen it to work from
              what is there now.
            </p>
          )}
          {annotation}
        </dd>
      </div>
    );
  }

  return (
    <div className="space-y-0.5 group">
      <dt className="flex items-baseline gap-2">
        <span className="text-caption text-stevens-ink-3">{resolveFieldLabel(field, data, sectionValues)}</span>
        <button
          type="button"
          onClick={open}
          /* B-450: hover-reveal keeps a twenty-row section from reading as
             twenty buttons, but a touch device has no hover — so the variant
             that hides this had nothing to lift it, and every edit control on
             this surface was invisible on a phone or tablet. `hover: none`
             restores it wherever the primary input cannot hover, and leaves the
             desktop density exactly as it was. */
          className="text-[11px] text-stevens-ink-3 underline underline-offset-2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity"
          aria-label={`Edit ${resolveFieldLabel(field, data, sectionValues)}`}
        >
          Edit
        </button>
      </dt>
      <dd className="text-[15px] text-foreground">
        {translatable
          ? <TranslatedAnswer original={stored as string} translation={translation} language={language} name={isName} translatable={answerTranslatable} onTranslate={onTranslate} />
          : shown}
      </dd>
      {retired && <p className="text-caption text-stevens-ink-3 italic">{RETIRED_NOTE}</p>}
      {conflict && (
        <p role="alert" className="text-caption text-status-danger-fg">
          {/* D-SE-5: this says the value on file DIFFERS. It never asserts a
              colleague changed it — the server cannot know that, and staff act
              on what this sentence says. */}
          Not saved — this field holds{' '}
          <strong>{answerAtPath(path, conflict.found, field, bcp47)}</strong> on file. Reopen it to work from
          what is there now.
        </p>
      )}
    </div>
  );
}

